// menu.js
import { trapFocus } from "../helpers/index.js";

const hamburger = document.getElementById("hamburger");
const nav = document.getElementById("nav");
const navBackground = document.querySelector(".background-blur");
const header = document.querySelector("header");

// Only proceed if navigation elements exist
if (!hamburger || !nav) {
  console.warn("Navigation elements not found, menu.js will not initialize");
  // Exit early if navigation elements don't exist
  throw new Error("Navigation elements not found");
}

// Store the cleanup function for the focus trap
let focusTrapCleanup = null;

// Add ARIA attributes to nav
nav.setAttribute("role", "navigation");
nav.setAttribute("aria-label", "Main navigation");

// Mobile submenu functionality
function initMobileSubmenus() {
  const menuItemsWithChildren = nav.querySelectorAll(".menu-item-has-children");

  menuItemsWithChildren.forEach((menuItem) => {
    const submenu = menuItem.querySelector("ul");
    const link = menuItem.querySelector("a");

    if (submenu && link) {
      // Set submenu title for mobile back button
      submenu.setAttribute("data-submenu-title", link.textContent.trim());

      // Create back button for mobile submenu
      const backButton = document.createElement("button");
      backButton.className = "submenu-back-button";
      backButton.setAttribute("aria-label", "Terug naar menu");
      backButton.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Terug</span>
      `;

      // Create wrapper for back button and parent item
      const submenuHeader = document.createElement("div");
      submenuHeader.className = "submenu-header";

      // Create parent menu item display
      const parentItem = document.createElement("div");
      parentItem.className = "submenu-parent-item";
      parentItem.textContent = link.textContent.trim();

      // Add back button and parent item to wrapper
      submenuHeader.appendChild(backButton);
      submenuHeader.appendChild(parentItem);

      // Insert wrapper at the beginning of the submenu
      submenu.insertBefore(submenuHeader, submenu.firstChild);

      // Add click handler for mobile submenu opening
      link.addEventListener("click", (e) => {
        // Only handle on mobile (960px = 60em breakpoint)
        if (window.innerWidth < 960) {
          e.preventDefault();
          e.stopPropagation(); // Prevent event bubbling
          openSubmenu(submenu);
        }
      });

      // Add touch handler for mobile devices
      link.addEventListener(
        "touchstart",
        (e) => {
          // Only handle on mobile (960px = 60em breakpoint)
          if (window.innerWidth < 960) {
            e.preventDefault();
            e.stopPropagation(); // Prevent event bubbling
            openSubmenu(submenu);
          }
        },
        { passive: false }
      );

      // Add back button functionality
      backButton.addEventListener("click", (e) => {
        e.preventDefault();
        closeSubmenu(submenu);
      });

      // Add touch handler for back button
      backButton.addEventListener(
        "touchstart",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          closeSubmenu(submenu);
        },
        { passive: false }
      );
    }
  });
}

function openSubmenu(submenu) {
  // Set display to block first, then add the class for the transform transition
  submenu.style.display = "block";
  submenu.style.opacity = "1";

  // Use requestAnimationFrame to ensure display: block is applied before the transform
  requestAnimationFrame(() => {
    submenu.classList.add("submenu-open");
  });

  // Focus first link in submenu
  const firstLink = submenu.querySelector("a");
  if (firstLink) {
    setTimeout(() => firstLink.focus(), 100);
  }
}

function closeSubmenu(submenu) {
  submenu.classList.remove("submenu-open");

  // Wait for the transition to complete before hiding the element
  setTimeout(() => {
    submenu.style.display = "none";
    submenu.style.opacity = "0";
  }, 400); // Match transition duration from CSS
}

// Function to toggle the menu visibility
function toggleMenu() {
  const isOpen = hamburger.getAttribute("aria-expanded") === "true" || false;

  // Toggle aria-expanded attribute
  hamburger.setAttribute("aria-expanded", String(!isOpen));

  // Toggle nav visibility using a class
  if (!isOpen) {
    openMenu();
  } else {
    closeMenu();
  }
}

// open menu
function openMenu() {
  // Close search if open
  const openSearch = document.querySelector("#open-search");
  const search = document.querySelector(".header-search");
  if (openSearch && search && search.classList.contains("active")) {
    search.classList.remove("active");
    openSearch.setAttribute("aria-expanded", "false");
    openSearch.classList.remove("active");
  }

  nav.classList.add("open");
  nav.setAttribute("aria-hidden", "false");
  navBackground.classList.add("active");

  // Re-enable focusable elements in the navigation
  const focusableElements = nav.querySelectorAll('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])');
  focusableElements.forEach((element) => {
    element.removeAttribute("tabindex");
  });

  // Add active class to header
  if (header) {
    header.classList.add("menu-active");
  }
}

// close menu
function closeMenu() {
  nav.classList.remove("open");
  nav.setAttribute("aria-hidden", "true");
  hamburger.setAttribute("aria-expanded", "false");
  navBackground.classList.remove("active");

  // Disable focusable elements in the navigation when hidden
  const focusableElements = nav.querySelectorAll('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])');
  focusableElements.forEach((element) => {
    element.setAttribute("tabindex", "-1");
  });

  // Remove active class from header
  if (header) {
    header.classList.remove("menu-active");
  }

  // Close any open submenus
  const openSubmenus = nav.querySelectorAll(".submenu-open");
  openSubmenus.forEach((submenu) => {
    closeSubmenu(submenu);
  });
}

// Initialize accessibility when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  // Set initial ARIA states
  nav.setAttribute("aria-hidden", "true");
  hamburger.setAttribute("aria-expanded", "false");
  hamburger.setAttribute("aria-controls", "nav");
  hamburger.setAttribute("aria-label", "Toggle navigation menu");

  // Disable focusable elements in the navigation by default
  const focusableElements = nav.querySelectorAll('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])');
  focusableElements.forEach((element) => {
    element.setAttribute("tabindex", "-1");
  });

  // Initialize mobile submenus
  initMobileSubmenus();
});

// Toggle the menu on click
hamburger.addEventListener("click", toggleMenu);

// Toggle the menu on touch for mobile devices
hamburger.addEventListener(
  "touchstart",
  (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleMenu();
  },
  { passive: false }
);

// Hide nav on keyup Escape
document.addEventListener("keyup", (e) => {
  if (e.code === "Escape") {
    // Close submenu if open, otherwise close main menu
    const openSubmenu = nav.querySelector(".submenu-open");
    if (openSubmenu) {
      closeSubmenu(openSubmenu);
    } else {
      closeMenu();
    }
  }
});

document.addEventListener("click", (e) => {
  const isHamburgerClick = e.target === hamburger || hamburger.contains(e.target);
  const isNavClick = e.target.closest("#nav") !== null;
  const isSearchClick = e.target.closest("#open-search") !== null || e.target.closest(".header-search") !== null;

  // Only close menu on click outside if not on mobile
  if (!isHamburgerClick && !isNavClick && !isSearchClick && window.innerWidth >= 960) {
    closeMenu();
  }
});

// Close the navigation on window resize (only on desktop)
window.addEventListener("resize", () => {
  if (window.innerWidth >= 960) {
    closeMenu();
  }
});
